import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
} from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AiChatOpenedVia } from '../../analytics/types/analytics-events.ts';
import { useAskPlatoChat } from '../hooks/use-ask-plato-chat.ts';
import { useAskPlatoEnabled } from '../hooks/use-ask-plato-enabled.ts';
import { AskPlatoPanel } from './ask-plato-panel.tsx';
import { minDockedViewportWidth, SidePanel } from './side-panel.tsx';

type AskPlatoContextType = {
    open: boolean;
    openAskPlato: (openedVia: AiChatOpenedVia) => void;
    closeAskPlato: () => void;
    toggleAskPlato: (openedVia: AiChatOpenedVia) => void;
    isDocked: boolean;
};

const AskPlatoContext = createContext<AskPlatoContextType | undefined>(undefined);

export function useAskPlato(): AskPlatoContextType {
    const context = useContext(AskPlatoContext);
    if (!context) {
        throw new Error('useAskPlato must be used within an AskPlatoProvider');
    }
    return context;
}

type PanelSession = {
    open: boolean;
    openedVia: AiChatOpenedVia;
};

/**
 * Set once the user has closed the panel for the first time. Until it is
 * present, the panel auto-opens on desktop so people discover it — the FAB
 * alone had a very low click rate.
 */
const autoOpenStorageKey = 'ask_plato_auto_opened';

/**
 * Owns the open state of the Ask Plato side panel and the ⌘J / Ctrl+J shortcut.
 *
 * The panel is non-modal, so unlike global search there is nothing to close
 * first — it can be opened from anywhere, at any moment, and stays open while
 * the user keeps navigating.
 *
 * Once the viewport can hold the app at its minimum width beside the panel at
 * its own, the panel docks instead of floating over the content — the layout
 * reads `isDocked` from here to reserve the room.
 *
 * Feature behind `enableAiChat`: outside it there is no panel and no
 * shortcut. The entry points are gated by their own render sites.
 */
export function AskPlatoProvider({ children }: PropsWithChildren) {
    const askPlatoEnabled = useAskPlatoEnabled();
    const sendEvent = useAnalytics();
    const isDocked = useMediaQuery(`(min-width: ${minDockedViewportWidth}px)`);
    // Matches the FAB's `sm:flex` — auto-open only where that entry point shows.
    const isDesktop = useBreakpoint('sm');
    // Open the panel once for people who have never closed it, so the feature
    // introduces itself instead of waiting behind the FAB. Closing it writes
    // the flag (see `setPanelOpen`), which keeps every later visit quiet.
    const [open, setOpen] = useState(
        () => askPlatoEnabled && isDesktop && localStorage.getItem(autoOpenStorageKey) === null,
    );
    const chat = useAskPlatoChat();
    // `open` still holds the auto-open decision when the ref initializes.
    const sessionRef = useRef<PanelSession>({ open, openedVia: open ? 'auto' : 'fab' });

    useEffect(() => {
        if (askPlatoEnabled) {
            sendEvent('ai_chat_entry_point_rendered');
        }
        // The auto-open happens in the `useState` initializer above; its open
        // event is reported here, once the panel is actually on screen.
        if (sessionRef.current.open) {
            sendEvent('ai_chat_opened', { opened_via: 'auto' });
        }
    }, [askPlatoEnabled, sendEvent]);

    // Every path in and out of the panel goes through here — the two entry
    // points, the shortcut, and the panel's own close button via the dialog
    // primitive's `onOpenChange` — so the open/close pair is reported once, and
    // the close reports the entry point that opened it.
    const setPanelOpen = useCallback(
        (next: boolean, openedVia: AiChatOpenedVia): void => {
            const session = sessionRef.current;
            if (session.open === next) {
                return;
            }

            if (next) {
                sessionRef.current = { open: true, openedVia };
                sendEvent('ai_chat_opened', { opened_via: openedVia });
            } else {
                sessionRef.current = { ...session, open: false };
                sendEvent('ai_chat_closed', { opened_via: session.openedVia });
                // Any close counts as "the user has seen the panel" — from here
                // on it only opens when asked to.
                localStorage.setItem(autoOpenStorageKey, 'true');
            }

            setOpen(next);
        },
        [sendEvent],
    );

    useEffect(() => {
        if (!askPlatoEnabled) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'j' || !(event.metaKey || event.ctrlKey)) {
                return;
            }
            event.preventDefault();
            setPanelOpen(!sessionRef.current.open, 'shortcut');
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [askPlatoEnabled, setPanelOpen]);

    const openAskPlato = useCallback(
        (openedVia: AiChatOpenedVia) => {
            setPanelOpen(true, openedVia);
        },
        [setPanelOpen],
    );

    const closeAskPlato = useCallback(() => {
        setPanelOpen(false, sessionRef.current.openedVia);
    }, [setPanelOpen]);

    const toggleAskPlato = useCallback(
        (openedVia: AiChatOpenedVia) => {
            setPanelOpen(!sessionRef.current.open, openedVia);
        },
        [setPanelOpen],
    );

    const value = useMemo(
        () => ({
            open: askPlatoEnabled && open,
            openAskPlato,
            closeAskPlato,
            toggleAskPlato,
            isDocked,
        }),
        [askPlatoEnabled, open, openAskPlato, closeAskPlato, toggleAskPlato, isDocked],
    );

    return (
        <AskPlatoContext.Provider value={value}>
            {askPlatoEnabled && (
                <SidePanel
                    open={open}
                    onOpenChange={(next) => {
                        setPanelOpen(next, sessionRef.current.openedVia);
                    }}
                >
                    <AskPlatoPanel chat={chat} docked={isDocked} />
                </SidePanel>
            )}
            {children}
        </AskPlatoContext.Provider>
    );
}
